import React, { useMemo } from 'react';

import { Box } from '@mui/material';
import { GridColDef, GridRowModel, GridRowsProp } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { isNumber } from 'lodash-es';

import { PedidoRealizadoProdutoProps, RequestPedidoRealizadoProdutoProps } from '../../types';

import { PanelGrid } from 'components/grid';
import { SubTitle } from 'components/text';
import { useListagemContext } from 'context/ListagemContext';
import { RequestOptionsType, useRequestListagem } from 'hooks/useRequestListagem';
import { brlPrice } from 'util/format';

interface PedidoRealizadoProdutosGrid {
    row: GridRowModel;
}

const getRows = (data: PedidoRealizadoProdutoProps[] | undefined): GridRowsProp => {
    if (Array.isArray(data) && data.length) {
        return data.map((item, index) => ({
            id: index + 1,
            vlTotalFaturado: item.vlTotalFaturado,
            vlUnitario: item.vlUnitario,
            qtProduto: item.qtProduto,
            qtProdutoFaturado: item.qtProdutoFaturado,
            cdProduto: item.produto.cdProduto,
            dsProduto: item.produto.dsProduto,
            dsSubgrupo: item.produto.subgrupo.dsSubgrupo,
            sgUnidadeMedida: item.produto.sgUnidadeMedida,
            qtMinimaCompraPedidoProduto: item.qtMinimaCompraPedidoProduto,
            lkImagem: item.produto.lkImagem,
        }));
    }

    return [];
};

export function PedidoRealizadoProdutosGrid({ row }: PedidoRealizadoProdutosGrid) {
    const { limit, page, sort } = useListagemContext();
    const RequestListagem = useRequestListagem();

    const requestOptions: RequestOptionsType = {
        url: '/compra/pedidoproduto',
        sort: sort?.length ? sort : [{ property: 'dsProduto', direction: 'ASC' }],
        filter: [{ property: 'idPedido', value: row.idPedido, operator: 'eq' }],
        limit,
        page,
    };

    const { fetchStatus, data, refetch } = useQuery(
        [requestOptions],
        (): Promise<RequestPedidoRealizadoProdutoProps> => RequestListagem(requestOptions).then((res) => res.data),
    );

    const columns = useMemo((): GridColDef[] => {
        const isFaturadoTotalOrParcial = ['FATURADO_PARCIALMENTE', 'FATURADO_TOTAL'].includes(row.pedidoStatus.pedidoStatusEnum);

        const baseColumns: GridColDef[] = [
            {
                field: 'imagem',
                align: 'center',
                headerAlign: 'center',
                headerName: 'Imagem',
                filterable: false,
                sortable: false,
                minWidth: 50,
                renderCell: ({ row }) => {
                    if (row.lkImagem) {
                        return (
                            <Box
                                sx={{
                                    minWidth: 50,
                                    minHeight: 50,
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundSize: 'contain',
                                    backgroundImage: `url("${row.lkImagem}")`,
                                    border: (theme) => `1px solid ${theme.palette.grey[400]}`,
                                    borderRadius: 1,
                                }}
                            />
                        );
                    }
                },
            },
            { field: 'cdProduto', headerName: 'Código do produto', width: 120, minWidth: 90 },
            { field: 'dsProduto', headerName: 'Produto', flex: 1, minWidth: 100 },
            { field: 'dsSubgrupo', headerName: 'Categoria', flex: 1, minWidth: 110 },
            {
                field: 'vlUnitario',
                headerName: 'Preço unitário',
                type: 'number',
                align: 'right',
                width: 130,
                minWidth: 110,
                valueFormatter: (value) => (isNumber(value) ? brlPrice(value) : ''),
            },
            {
                field: 'qtMinimaCompraPedidoProduto',
                headerName: 'Quantidade mínima',
                type: 'number',
                width: 130,
                minWidth: 100,
                renderCell: ({ value, row }) => (isNumber(value) ? `${value} ${row.sgUnidadeMedida}` : ''),
            },
            {
                field: 'qtProduto',
                headerName: 'Quantidade pedido',
                type: 'number',
                width: 130,
                minWidth: 100,
                renderCell: ({ value, row }) => (isNumber(value) ? `${value} ${row.sgUnidadeMedida}` : ''),
            },
        ];

        if (isFaturadoTotalOrParcial) {
            baseColumns.splice(
                7,
                0,
                {
                    field: 'qtProdutoFaturado',
                    headerName: 'Quantidade faturado',
                    type: 'number',
                    width: 130,
                    minWidth: 100,
                    sortable: false,
                    renderCell: ({ value, row }) => (isNumber(value) ? `${value} ${row.sgUnidadeMedida}` : ''),
                },
                {
                    field: 'vlTotalFaturado',
                    headerName: 'Total faturado',
                    type: 'number',
                    align: 'right',
                    valueFormatter: (value) => (isNumber(value) ? brlPrice(value) : ''),
                    sortable: false,
                    width: 130,
                    minWidth: 110,
                },
            );
        }

        return baseColumns;
    }, []);

    return (
        <React.Fragment>
            <SubTitle label={'Lista de Produtos'} sx={{ ml: 2, mt: 2, mb: 2 }} />

            <Box sx={{ ml: 2, mr: 2, mb: 2 }}>
                <PanelGrid
                    rows={getRows(data?.data)}
                    rowsCount={data?.total ?? 0}
                    reload={refetch}
                    loading={fetchStatus === 'fetching'}
                    columns={columns}
                />
            </Box>
        </React.Fragment>
    );
}
